import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FAQPage, FAQItem } from './FAQComponent';
import { ContactForm } from './ContactForm';
import { UserDto, CompanyDto } from '@jaam/commons/dtos';
import { useGetFAQs } from '../../hooks/useGetFAQs';

const STATIC_FAQ_IDS = ['1', '2', '3', '4', '5', '6'] as const;

function useI18nFallbackFaqs(t: (key: string, options?: { defaultValue?: string }) => string): FAQItem[] {
  return useMemo(
    () =>
      STATIC_FAQ_IDS.map((id) => ({
        id: `i18n-faq-${id}`,
        question: t(`faq.items.${id}.question`, { defaultValue: '' }),
        answer: t(`faq.items.${id}.answer`, { defaultValue: '' }),
      })).filter((item) => item.question.length > 0 && item.answer.length > 0),
    [t],
  );
}

interface HelpPageProps {
  activitySectorOptions?: { value: string | number; label: string }[];
  isLoadingSectors?: boolean;
  user?: UserDto;
  activeCompany?: CompanyDto | null;
  faqCategory?: string;
  onSubmitContactForm?: (data: any) => Promise<void>;
}

export const HelpPage: React.FC<HelpPageProps> = ({
  activitySectorOptions,
  isLoadingSectors,
  user,
  activeCompany,
  faqCategory,
  onSubmitContactForm,
}) => {

  const { t } = useTranslation();
  const supportEmail = 'contact@jaam.cloud';
  const { faqs, isFetchingFAQs } = useGetFAQs({
    category: faqCategory,
    enabled: true,
  });
  const fallbackFaqs = useI18nFallbackFaqs(t);
  const displayFaqs = faqs.length > 0 ? faqs : fallbackFaqs;

  return (
    <div className="flex flex-col h-full overflow-y-auto max-h-[calc(100vh-64px)] md:px-4">
      {/* Header - Fixed */}
      <div className="relative flex items-center justify-start pt-4 pb-10 md:pt-8 md:pb-20 ">
        <h1 className="text-[45px] leading-[52px]  text-neutral-20">
          {t('help.title')}
        </h1>

      </div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 ">
        <div className="mx-auto ">
          <div className="grid grid-cols-1 gap-8 md:gap-20 lg:grid-cols-2">

            <div className="">
              <h2 className="mb-8 text-4xl font-light text-neutral-20">
                {t('help.faq.title')}
              </h2>
              <p className="text-base leading-relaxed text-neutral-40">
                {t('help.faq.description')}
              </p>
              <p className="mb-8 text-base leading-relaxed text-neutral-40">
                {t('help.faq.supportText')} <a
                  href={`mailto:${supportEmail}`}
                  className="underline text-primary hover:underline"
                >
                  {supportEmail}
                </a>
              </p>


            </div>
            <FAQPage faqs={displayFaqs} isLoading={isFetchingFAQs} />

          </div>
          <div className="grid grid-cols-1 gap-8 mt-20 md:gap-20 lg:grid-cols-2">

            {/* We're here to help Section */}
            <div className="">
              <h2 className="mb-4 text-4xl font-light text-neutral-20">
                {t('help.contact.title')}
              </h2>
              <p className="mb-4 text-base leading-relaxed text-neutral-40">
                {t('help.contact.description1')}
              </p>
              <p className="mb-4 text-base leading-relaxed text-neutral-40">
                {t('help.contact.description2')} <a
                  href={`mailto:${supportEmail}`}
                  className="underline text-primary hover:underline"
                >
                  {supportEmail}
                </a>
              </p>


            </div>
            <ContactForm
              activitySectorOptions={activitySectorOptions}
              isLoadingSectors={isLoadingSectors}
              user={user}
              activeCompany={activeCompany}
              onSubmit={onSubmitContactForm}
            />


          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
